import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  message: "",
  production_name: "",
  package_tier: "",
  crew_count: "",
  location: "",
  shoot_dates: "",
  dietary_requirements: "",
};

const Field = ({ label, id, children }) => (
  <div>
    <label htmlFor={id} className="mb-1 block font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
      {label}
    </label>
    {children}
  </div>
);

export const EnquiryForm = ({ variant = "general", presetTier }) => {
  const isCatering = variant === "catering";
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");
  const p = isCatering ? "catering" : "contact";

  useEffect(() => {
    if (isCatering && presetTier) {
      setForm((f) => ({ ...f, package_tier: presetTier }));
    }
  }, [presetTier, isCatering]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/enquiries`, { ...form, form_type: variant });
      setStatus("sent");
      setForm(initial);
      toast.success("Enquiry sent — we'll be in touch shortly.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (status === "sent") {
    return (
      <div className="flex flex-col items-start border border-gold/30 bg-elevated/40 p-10" data-testid={`${p}-form-success`} role="status">
        <CheckCircle2 size={40} className="text-leaf-light" />
        <h3 className="mt-6 font-editorial text-3xl tracking-tight text-cream">Thank you — your enquiry has been sent.</h3>
        <p className="mt-4 max-w-sm text-sm leading-relaxed text-sage">
          We'll be in touch soon — a real human from the Root2Shoot team will reply within one working day.
        </p>
        <button onClick={() => setStatus("idle")} className="btn-ghost mt-8 !px-5 !py-2.5" data-testid={`${p}-form-send-another`}>
          Send Another Enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-x-8 gap-y-7 sm:grid-cols-2" data-testid={`${p}-enquiry-form`} noValidate={false}>
      <Field label="Your Name *" id={`${p}-name`}>
        <input id={`${p}-name`} data-testid={`${p}-input-name`} className="input-field" required value={form.name} onChange={set("name")} placeholder="Full name" autoComplete="name" />
      </Field>
      <Field label="Email *" id={`${p}-email`}>
        <input id={`${p}-email`} data-testid={`${p}-input-email`} type="email" className="input-field" required value={form.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" />
      </Field>
      <Field label="Phone" id={`${p}-phone`}>
        <input id={`${p}-phone`} data-testid={`${p}-input-phone`} type="tel" className="input-field" value={form.phone} onChange={set("phone")} placeholder="Optional" autoComplete="tel" />
      </Field>

      {isCatering && (
        <>
          <Field label="Production Name" id="catering-production">
            <input id="catering-production" data-testid="catering-input-production" className="input-field" value={form.production_name} onChange={set("production_name")} placeholder="Film, TV show or production" />
          </Field>
          <Field label="Package Tier" id="catering-tier">
            <select id="catering-tier" data-testid="catering-select-tier" className="input-field" value={form.package_tier} onChange={set("package_tier")}>
              <option value="">Not sure yet</option>
              <option value="essential">Essential</option>
              <option value="enhanced">Enhanced</option>
              <option value="luxury">Luxury</option>
            </select>
          </Field>
          <Field label="Crew Size" id="catering-crew">
            <input id="catering-crew" data-testid="catering-input-crew" type="number" min="1" className="input-field" value={form.crew_count} onChange={set("crew_count")} placeholder="e.g. 45" />
          </Field>
          <Field label="Location" id="catering-location">
            <input id="catering-location" data-testid="catering-input-location" className="input-field" value={form.location} onChange={set("location")} placeholder="Studio, location or city" />
          </Field>
          <Field label="Shoot Dates" id="catering-dates">
            <input id="catering-dates" data-testid="catering-input-dates" className="input-field" value={form.shoot_dates} onChange={set("shoot_dates")} placeholder="e.g. 12 – 28 August" />
          </Field>
          <Field label="Dietary Requirements" id="catering-dietary">
            <input id="catering-dietary" data-testid="catering-input-dietary" className="input-field" value={form.dietary_requirements} onChange={set("dietary_requirements")} placeholder="Vegan, GF, halal, allergies…" />
          </Field>
        </>
      )}

      <div className="sm:col-span-2">
        <Field label={isCatering ? "Tell Us About the Job *" : "Your Message *"} id={`${p}-message`}>
          <textarea id={`${p}-message`} data-testid={`${p}-input-message`} className="input-field min-h-[120px] resize-y" required value={form.message} onChange={set("message")} placeholder={isCatering ? "Dates, schedule, vibe, anything that helps us plan…" : "How can we help?"} />
        </Field>
      </div>

      <div className="sm:col-span-2">
        <button type="submit" disabled={status === "sending"} className="btn-gold w-full disabled:opacity-60 sm:w-auto" data-testid={`${p}-form-submit`}>
          {status === "sending" && <Loader2 size={16} className="animate-spin" />}
          {status === "sending" ? "Sending…" : isCatering ? "Send Catering Enquiry" : "Send Message"}
        </button>
      </div>
    </form>
  );
};
